import { useMinuteTicker } from '@/hooks/useMinuteTicker'
import { useNewsListQuery } from '@/queries/home/homeQueries'
import { NewsArticle } from '@components/organism/common/NewsArticle.jsx'
import { SectionTitle } from '@components/organism/common/SectionTitle.jsx'
import { usePolling } from '@hooks/usePolling.js'
import { useTranslation } from 'react-i18next'
import { MdKeyboardDoubleArrowRight } from 'react-icons/md'
import { Link } from 'react-router-dom'
import { ErrorUI } from '@components/organism/common/ErrorUI.jsx'
import { LoadingCom } from '@components/organism/common/LoadingCom.jsx'

const NEWS_SECTION_MODE = {
  PAGE: 'page',
  WIDGET: 'widget'
}

const NEWS_SECTION_CONFIG = {
  [NEWS_SECTION_MODE.PAGE]: {
    listPath: '/news',
    detailPath: (key) => `/news/${key}`,
    detailState: undefined,
    headerClassName: ''
  },
  [NEWS_SECTION_MODE.WIDGET]: {
    listPath: '/widgets/news/list',
    detailPath: (key) => `/widgets/news/${key}`,
    detailState: { from: '/widgets/news' },
    headerClassName: 'flex items-center justify-between'
  }
}

const NEWS_REFRESH_PERIOD = '5m-20s'

const NewsSection = ({ isWidget = false }) => {
  const { t } = useTranslation()
  const { data, refetch, isError, isLoading } = useNewsListQuery({ page: 1, count: 5 })
  useMinuteTicker()
  const currentTime = new Date()

  const mode = isWidget ? NEWS_SECTION_MODE.WIDGET : NEWS_SECTION_MODE.PAGE
  const config = NEWS_SECTION_CONFIG[mode]

  usePolling(refetch, NEWS_REFRESH_PERIOD, { enabled: !isWidget })

  return (
    <section className={'x-spacing y-spacing bg-white'}>
      <div className={config.headerClassName}>
        <SectionTitle
          title={t(isWidget ? 'widget.news' : 'home.news.title')}
          to={isWidget ? undefined : config.listPath}
        />
        {isWidget && (
          <Link to={config.listPath} className="flex items-center gap-1">
            {t('common.more')}
            <MdKeyboardDoubleArrowRight size={20} />
          </Link>
        )}
      </div>
      {isLoading ? (
        <LoadingCom height="668" />
      ) : isError ? (
        <ErrorUI height="668" handleClick={refetch} />
      ) : (
        <section className={'pt-24'}>
          {Object.entries(data?.newsVOS || {})?.map(
            ([_, { key, pressNm, publishedDt, title, thumbnailLnk }]) => (
              <Link
                to={config.detailPath(key)}
                key={key}
                state={config.detailState}
                className="last:[&>article]:border-b-0"
              >
                <NewsArticle
                  title={title}
                  pressNm={pressNm}
                  thumbnailLnk={thumbnailLnk}
                  publishedDt={publishedDt}
                  currentTime={currentTime}
                />
              </Link>
            )
          )}
        </section>
      )}
    </section>
  )
}

export default NewsSection
